import { formatShortDate } from '../../../../ts-utils/date-utils'
import { formatRegistrationDateRangeParts } from '../../../../ts-utils/registration-datetime'
import type { OrgRegistration } from '../types'
import {
  buildOrgRegistrationCardModel,
  type OrgRegistrationCardModel,
} from './cardModel'

/** A registration's own row in the list view. */
export interface OrgRegistrationSummaryRow {
  kind: 'registration'
  id: string
  registration: OrgRegistration
  model: OrgRegistrationCardModel
  /** Its divisions row is open under it. */
  isExpanded: boolean
  /** The registration window's ends, a line each; '' when unset. */
  windowStart: string
  windowEnd: string
  /** The season's first day, e.g. 'Nov 1, 2026'; '' when unset. */
  seasonStart: string
}

/** The divisions table opened under a registration's row. */
export interface OrgRegistrationDetailRow {
  kind: 'detail'
  id: string
  /** The `id` of the summary row it sits under. */
  parentId: string
  registration: OrgRegistration
  model: OrgRegistrationCardModel
}

export type OrgRegistrationListRow =
  | OrgRegistrationSummaryRow
  | OrgRegistrationDetailRow

/** The detail row's id — distinct from every registration id. */
export const getOrgRegistrationDetailRowId = (registrationId: string): string =>
  `${registrationId}:detail`

/**
 * The list view's rows: one per registration, in the API's order, each
 * expanded one followed by its divisions row. `now` is a parameter so every
 * row counts down from the same moment.
 */
export const buildOrgRegistrationListRows = (
  registrations: readonly OrgRegistration[],
  expandedIds: ReadonlySet<string>,
  now: Date = new Date()
): OrgRegistrationListRow[] =>
  registrations.flatMap((registration): OrgRegistrationListRow[] => {
    const model = buildOrgRegistrationCardModel(registration, now)
    const window = formatRegistrationDateRangeParts(
      registration.opensAt,
      registration.closesAt,
      registration.timezone
    )
    const isExpanded = expandedIds.has(registration.id)
    const summary: OrgRegistrationSummaryRow = {
      kind: 'registration',
      id: registration.id,
      registration,
      model,
      isExpanded,
      windowStart: window.start,
      windowEnd: window.end,
      // A calendar date (UTC midnight), so read in UTC — not a zone's instant.
      seasonStart: registration.seasonStartsAt
        ? formatShortDate(registration.seasonStartsAt)
        : '',
    }
    if (!isExpanded) {
      return [summary]
    }
    return [
      summary,
      {
        kind: 'detail',
        id: getOrgRegistrationDetailRowId(registration.id),
        parentId: registration.id,
        registration,
        model,
      },
    ]
  })

/**
 * Re-seats every detail row directly under its registration's row, after the
 * grid has sorted. A sort compares the detail rows too and would scatter them;
 * this keeps the summary rows in the sorted order and moves only the details.
 * A detail whose parent is missing is dropped.
 *
 * @param items - the sorted items (grid row nodes, or the rows themselves)
 * @param getRow - reads an item's row
 */
export const placeDetailRowsUnderParents = <T>(
  items: readonly T[],
  getRow: (item: T) => OrgRegistrationListRow | undefined
): T[] => {
  const detailsByParent = new Map<string, T>()
  items.forEach((item) => {
    const row = getRow(item)
    if (row?.kind === 'detail') {
      detailsByParent.set(row.parentId, item)
    }
  })

  return items.flatMap((item) => {
    const row = getRow(item)
    if (row?.kind === 'detail') {
      return []
    }
    const detail = row ? detailsByParent.get(row.id) : undefined
    return detail ? [item, detail] : [item]
  })
}
